import { useState } from "react"
import { z } from "zod"

import {
	Button,
	Checkbox,
	Dialog,
	Form,
	FormInput,
	RadioGroup,
	RadioGroupItem,
	SecretReveal,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SCOPE_GROUPS } from "./scope-groups"

const createApiKeySchema = z.object({
	label: z.string().min(1, "Required"),
})

const DEFAULT_CUSTOM_SCOPES = new Set<string>(["threads:read"])

export function CreateApiKeyDialog({
	isOpen,
	onClose,
}: {
	isOpen: boolean
	onClose: () => void
}) {
	const ctx = api.useUtils()
	const form = useZodForm({ schema: createApiKeySchema })
	const [preset, setPreset] = useState<"full" | "custom">("full")
	const [customScopes, setCustomScopes] = useState<Set<string>>(
		new Set(DEFAULT_CUSTOM_SCOPES),
	)
	const [createdSecret, setCreatedSecret] = useState<string | null>(null)

	const createMutation = api.workspace.createApiKeyV2.useMutation({
		onSuccess: async (data) => {
			await ctx.workspace.invalidate()
			setCreatedSecret(data.secret)
			await navigator.clipboard.writeText(data.secret)
			toast.success({ title: "API key copied" })
		},
	})

	const close = () => {
		onClose()
		setTimeout(() => {
			form.reset()
			setPreset("full")
			setCustomScopes(new Set(DEFAULT_CUSTOM_SCOPES))
			setCreatedSecret(null)
		}, 200)
	}

	const toggleScope = (id: string) => {
		setCustomScopes((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const customCount = customScopes.size

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={close}
			title={createdSecret ? "Save your key" : "Create API key"}
			footer={
				createdSecret ? undefined : (
					<Button
						type="submit"
						form="create-api-key-form"
						isLoading={createMutation.isLoading}
						disabled={preset === "custom" && customCount === 0}
					>
						Create
					</Button>
				)
			}
		>
			{createdSecret ? (
				<SecretReveal
					value={createdSecret}
					description="Save this API key in a secure location. If you lose it, you'll need to generate a new one."
					onCopied={() => toast.success({ title: "API key copied" })}
					onClose={close}
				/>
			) : (
				<Form
					id="create-api-key-form"
					form={form}
					onSubmit={(values) => {
						createMutation.mutate({
							label: values.label,
							scopes:
								preset === "full"
									? { preset: "full" }
									: {
											preset: "custom",
											values: Array.from(customScopes) as never,
										},
						})
					}}
				>
					<FormInput
						name="label"
						label="Label"
						placeholder="A descriptive name for this API key…"
					/>
					<div className="mt-6">
						<div className="text-sm font-medium">Access</div>
						<div className="mb-3 text-xs text-secondary">
							Only enable the minimum permissions required for this key.
						</div>
						<RadioGroup
							value={preset}
							onValueChange={(v) => setPreset(v as "full" | "custom")}
						>
							<RadioGroupItem value="full" label="Full access" />
							<RadioGroupItem value="custom" label="Only select permissions…" />
						</RadioGroup>
						{preset === "custom" && (
							<div className="mt-3 flex max-h-72 flex-col gap-4 overflow-y-auto pl-7">
								{SCOPE_GROUPS.map((group) => (
									<div key={group.title}>
										<div className="mb-2 text-xs font-medium text-primary">
											{group.title}
										</div>
										<div className="flex flex-col gap-2">
											{group.scopes.map((s) => (
												<Checkbox
													key={s.id}
													size="sm"
													checked={customScopes.has(s.id)}
													onCheckedChange={() => toggleScope(s.id)}
													label={
														<span className="flex items-end gap-2">
															<span className="font-mono text-xs text-secondary">
																{s.id}
															</span>
															•
															<span className="text-sm text-primary">
																{s.label}
															</span>
														</span>
													}
												/>
											))}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</Form>
			)}
		</Dialog>
	)
}
